// Spread for button has type

import { MenuOutlined, CloseOutlined } from "@ant-design/icons";
import { Grid, Layout, Flex, Menu, MenuProps, Button } from "antd";
import React, { useState } from "react";
import { useCollapse } from "react-collapsed";
import { useLocation, To, Link } from "react-router";

import { useFirebaseAuth } from "@/contexts/UserProfileContext/UserProfileContext";
import { useUserOrganization } from "@/utils/partnerData";

import ErrorHandler from "@/components/ErrorHandler";
import { signOut } from "firebase/auth";
import LinkButton from "@/components/LinkButton";
import { auth } from "@/firebase";

const { useBreakpoint } = Grid;

const handleLogout = async () => {
    await signOut(auth);
    // Set session storage to defaults
    sessionStorage.setItem("setStudyUrl", "");
    sessionStorage.setItem("myData", "");
    localStorage.setItem("emailForSignIn", "");
    localStorage.removeItem("emailLinkRedirectTo");
};
function LogInButton() {
    const location = useLocation();

    let redirectTo: To;
    if (location.pathname === "/sign_in" || location.pathname === "/sign_up") {
        redirectTo = location; // Prevent the redirects from stacking if user clicks this while already on signup/signin page
    } else {
        redirectTo = {
            pathname: "/sign_in",
            search: `?redirectTo=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
        };
    }

    return (
        <Link className="navbar-item" to={redirectTo}>
            Sign In / Create Account
        </Link>
    );
}

type MenuItem = Required<MenuProps>["items"][number];

function getMenuItems(userOrganization?: string): MenuItem[] {
    if (userOrganization === "ecrf") {
        return [
            {
                label: (
                    <Link className="navbar-item" to="/#trialFinder">
                        Find a Study
                    </Link>
                ),
                key: "findStudy", // TODO: Scroll to trial finder on navigation
            },
            {
                label: <span className="navbar-item">Learn More</span>,
                key: "learnMore",
                children: [
                    {
                        label: <Link to="/overview">Learn about Research Studies</Link>,
                        key: "learnMore:page",
                    },
                    {
                        label: <Link to="/glossary">Glossary</Link>,
                        key: "learnMore:glossary",
                    },
                ],
            },
            {
                label: (
                    <Link className="navbar-item" to="/faq">
                        FAQ
                    </Link>
                ),
                key: "faq",
            },
        ];
    }

    return [
        {
            label: (
                <Link className="navbar-item" to="/">
                    Home
                </Link>
            ),
            key: "home",
        },
        {
            label: (
                <Link className="navbar-item" to="/#trialFinder">
                    Find a Study
                </Link>
            ),
            key: "findStudy", // TODO: Scroll to trial finder on navigation
        },
        {
            label: <span className="navbar-item">Learn More</span>,
            key: "learnMore",
            children: [
                {
                    label: <Link to="/overview">Learn about Research Studies</Link>,
                    key: "learnMore:page",
                },
                {
                    label: <Link to="/glossary">Glossary</Link>,
                    key: "learnMore:glossary",
                },
            ],
        },
        {
            label: <span className="navbar-item">About Us</span>,
            key: "aboutUs",
            children: [
                {
                    label: <Link to="/about">Who We Are</Link>,
                    key: "aboutUs:whoWeAre",
                },
                {
                    label: <Link to="/contact_us">Contact Us</Link>,
                    key: "aboutUs:contact_us",
                },
                {
                    label: <Link to="/benefits">Benefits of ResearchLinQ</Link>,
                    key: "aboutUs:benefits",
                },
            ],
        },
        {
            label: (
                <Link className="navbar-item" to="/faq">
                    FAQ
                </Link>
            ),
            key: "faq",
        },
    ];
}

function useMenuItems(isAnonymous: boolean, userOrganization?: string) {
    const { pathname } = useLocation();

    if (pathname === "/complete_signup") {
        return [];
    }
    const items: MenuItem[] = getMenuItems(userOrganization);

    const LoginMenu = {
        label: <LogInButton />,
        key: "Login",
    };

    const DashboardMenu = {
        label: (
            <LinkButton type="primary" to="/account">
                My Studies
            </LinkButton>
        ),
        key: "dashboard",
        children: [
            {
                label: <Link to="/account">My Studies</Link>,
                key: "profile:viewDashboard",
            },
            {
                label: (
                    <Link onClick={handleLogout} to="/">
                        Sign Out
                    </Link>
                ),
                key: "profile:logout",
            },
        ],
    };

    return isAnonymous ? [...items, LoginMenu] : [...items, DashboardMenu];
}

function Header() {
    const screens = useBreakpoint();
    const [prevScreens, setPrevScreens] = useState(screens); // Track changes to know when to close Hamburger menu
    const { getCollapseProps, getToggleProps, isExpanded, setExpanded } = useCollapse();
    const { isAnonymous } = useFirebaseAuth();
    const { userOrganization } = useUserOrganization();
    const { pathname } = useLocation();
    const [isDismissed, setIsDismissed] = useState(false);

    const menuItems = useMenuItems(isAnonymous, userOrganization.key);

    if (screens !== prevScreens) {
        setExpanded(false);
        setPrevScreens(screens);
    }

    let preHeader: React.ReactNode;
    if (userOrganization.key === "ecrf" && !isDismissed) {
        preHeader = (
            <div
                style={{
                    background: "white",
                    textAlign: "center",
                    lineHeight: "2rem",
                    fontSize: "1.2rem",
                    color: "#280652",
                }}
            >
                ECRF has collaborated with our trusted partner ResearchLinQ to bring you the ECRF Trial Finder.
                <Button type="text" icon={<CloseOutlined />} onClick={() => setIsDismissed(true)} />
            </div>
        );
    }

    return (
        <Layout.Header className="header">
            {preHeader}
            <ErrorHandler>
                <Flex justify="space-between" align="center" className="header-container">
                    <Link id="logo" to="/">
                        {userOrganization.logo}
                    </Link>

                    {screens.lg ? (
                        // TODO: Fix error message on Menu unmount
                        <nav>
                            <Menu
                                theme={userOrganization.headerMenuTheme ?? "light"}
                                id="menu-horizontal"
                                disabledOverflow // Prevents menu from collapsing on Firefox. Prop name may change in future antd version
                                mode="horizontal"
                                items={pathname === "/complete_signup" ? [] : menuItems}
                            />
                        </nav>
                    ) : pathname === "/complete_signup" ? null : (
                        <button className="navbar-menu-icon" {...getToggleProps()}>
                            {!isExpanded ? <MenuOutlined /> : <CloseOutlined />}
                        </button>
                    )}
                </Flex>
                <nav {...getCollapseProps()}>
                    <Menu id="menu-vertical" onClick={() => setExpanded(false)} mode="inline" items={menuItems} />
                </nav>
            </ErrorHandler>
        </Layout.Header>
    );
}

export default Header;
